import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CalendarClock, Check, FileText, Handshake, Receipt } from "lucide-react";
import { cn } from "@/lib/utils";
import { PayoutLetterWizard } from "@/pages/collections/PayoutLetterWizard";
import { StatementDialog } from "@/pages/collections/StatementDialog";
import { PaymentPlanWizard } from "@/pages/collections/PaymentPlanWizard";
import { PromiseCaptureDialog } from "@/pages/inbox/PromiseCaptureDialog";

// Accounts-only quick actions for the matched customer file — the same
// wizards the Customer File timeline uses, opened right from the inbox.
export function AccountsQuickActions({ matched, email, api, me, onPromiseAdded }) {
  const [active, setActive] = useState(null);
  if (!matched) return null;
  const caseRow = { case_id: matched.case_id, customer_name: matched.customer_name, loan_application_id: matched.sf };
  const close = () => setActive(null);
  const t = email?.triage;
  const isPromise = t?.category === "Promise to pay" || !!t?.promised_date;
  const added = t?.p2p;
  const actions = [
    { id: "statement", label: "Statement", icon: FileText, cls: "text-emerald-700 border-emerald-300", testid: "qa-statement-btn" },
    { id: "payout", label: "Payout letter", icon: Receipt, cls: "text-indigo-700 border-indigo-300", testid: "qa-payout-btn" },
    { id: "plan", label: "Payment plan", icon: CalendarClock, cls: "text-violet-700 border-violet-300", testid: "qa-plan-btn" },
  ];
  return (
    <>
      <span className="text-[10px] uppercase tracking-wide text-slate-400 mr-0.5">Quick actions</span>
      {email && (
        <Button size="sm" variant={isPromise && !added ? "default" : "outline"} onClick={() => setActive("p2p")} data-testid="qa-promise-btn"
          className={cn("h-7 text-[11px]", isPromise && !added ? "bg-amber-600 hover:bg-amber-700 text-white" : "text-amber-800 border-amber-300")}
          title={added ? `Promise already added: ${api.currency}${added.promised_amount} on ${added.promised_at} — click to add another` : "Turn this email into a promise to pay on the file and draft the confirmation reply"}>
          {added ? <Check className="w-3 h-3 mr-1" /> : <Handshake className="w-3 h-3 mr-1" />}{added ? "Promise added" : "Promise to pay"}
        </Button>
      )}
      {actions.map(a => (
        <Button key={a.id} size="sm" variant="outline" className={`h-7 text-[11px] ${a.cls}`} onClick={() => setActive(a.id)} data-testid={a.testid}
          title={`${a.label} for ${matched.sf || matched.customer_name}`}>
          <a.icon className="w-3 h-3 mr-1" />{a.label}
        </Button>
      ))}
      {active === "statement" && <StatementDialog caseRow={caseRow} onClose={close} onDone={close} />}
      {active === "payout" && <PayoutLetterWizard caseRow={caseRow} onClose={close} onDone={close} />}
      {active === "plan" && <PaymentPlanWizard caseRow={caseRow} onClose={close} onDone={close} />}
      {active === "p2p" && <PromiseCaptureDialog email={email} api={api} me={me} matched={matched} onClose={close} onAdded={(r) => { close(); onPromiseAdded?.(r); }} />}
    </>
  );
}
